"use client";

import {ChangeEvent, JSX} from "react";

import {useSnapshot} from "valtio";

import {useTranslation} from "@/shared/i18n";
import {type RangeInstance} from "@/shared/models/form/fields/range";

import "./styles/range-slider-field.scss";

interface RangeSliderFieldProps {
    model: RangeInstance;
    min: number;
    max: number;
    step?: number;
    className?: string;
    /**
     * Optional formatter function to display custom labels
     * @param value - The numeric value
     * @returns Formatted string to display
     */
    formatLabel?: (value: number) => string;
}

export function RangeSliderField({model, min, max, step = 1, className, formatLabel}: RangeSliderFieldProps): JSX.Element {
    const {t} = useTranslation("filters");
    const snapshot = useSnapshot(model);

    const currentMin = snapshot.minValue ?? min;
    const currentMax = snapshot.maxValue ?? max;

    // Use custom formatter if provided, otherwise default to number
    const formatValue = formatLabel || ((value: number) => String(value));

    const onMinInput = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = Number(e.target.value);
        const clamped = Math.min(Math.max(value, min), currentMax);
        model.setMinValueInput(clamped);
    };

    const onMaxInput = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = Number(e.target.value);
        const clamped = Math.max(Math.min(value, max), currentMin);
        model.setMaxValueInput(clamped);
    };

    const onMinChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = Number(e.target.value);
        const clamped = Math.min(Math.max(value, min), currentMax);
        model.setMinValue(clamped);
    };

    const onMaxChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = Number(e.target.value);
        const clamped = Math.max(Math.min(value, max), currentMin);
        model.setMaxValue(clamped);
    };

    // Compute progress bar positions
    const leftPercent = ((currentMin - min) / (max - min)) * 100;
    const rightPercent = 100 - ((currentMax - min) / (max - min)) * 100;

    return (
        <div className={`range-slider-field ${className || ""}`}>
            <div className="range-slider-field__labels">
                <span className="range-slider-field__edge">{formatValue(currentMin)}</span>
                <span className="range-slider-field__edge">{formatValue(currentMax)}</span>
            </div>

            <div className="range-slider-field__track">
                <div className="range-slider-field__progress" style={{left: `${leftPercent}%`, right: `${rightPercent}%`}} />
            </div>

            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={currentMin}
                onInput={onMinInput}
                onChange={onMinChange}
                className="range-slider-field__input range-slider-field__input--min"
                aria-label={t("minimumPrice")}
            />
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={currentMax}
                onInput={onMaxInput}
                onChange={onMaxChange}
                className="range-slider-field__input range-slider-field__input--max"
                aria-label={t("maximumPrice")}
            />
        </div>
    );
}
